/**
 * Composant OverlayPlayer - À utiliser comme source navigateur dans OBS.
 * Écoute les événements Socket.io pour afficher les overlays associés aux récompenses Twitch.
 * URL OBS : {origin}/overlay-player
 */

import React, { useMemo } from 'react';
import socketService from '../../services/SocketService';
import '../../styles/OverlayPlayer.css';
import { useOverlayPlayback } from './useOverlayPlayback';
import { useLeaderboard } from './useLeaderboard';
import { useOverlaySocket } from './useOverlaySocket';
import { useBattleOverlay } from './useBattleOverlay';
import { BirthdayOverlayContent } from './BirthdayOverlay';
import { OverlayLeaderboardPodium } from './OverlayLeaderboard';
import { getOverlayDisplayTitle } from './overlayUtils';
import BattleOverlay from './BattleOverlay';
import {
  BATTLE_OVERLAY_COUNTDOWN_PREVIEW_QUERY,
  BATTLE_OVERLAY_COUNTDOWN_PREVIEW_MAX_SEC,
} from '../../config/battleViewersGuideSteps';

/**
 * Lit les paramètres d'URL (debug, userId, type, preview décompte battle) côté client.
 * @returns {{ debug: boolean, leaderboardUserIdParam: string, leaderboardTypeParam: string, battleCountdownPreviewSec: number }}
 */
function useOverlayUrlParams() {
  return useMemo(() => {
    if (typeof window === 'undefined') {
      return {
        debug: false,
        leaderboardUserIdParam: '',
        leaderboardTypeParam: 'first',
        battleCountdownPreviewSec: 0,
      };
    }
    const searchParams = new URLSearchParams(window.location.search);
    const rawPreview = searchParams.get(BATTLE_OVERLAY_COUNTDOWN_PREVIEW_QUERY);
    const previewParsed =
      rawPreview != null && rawPreview !== ''
        ? parseInt(rawPreview, 10)
        : NaN;
    const battleCountdownPreviewSec =
      Number.isFinite(previewParsed) && previewParsed > 0
        ? Math.min(Math.floor(previewParsed), BATTLE_OVERLAY_COUNTDOWN_PREVIEW_MAX_SEC)
        : 0;

    return {
      debug: searchParams.has('debug'),
      leaderboardUserIdParam: searchParams.get('userId')?.trim() || '',
      leaderboardTypeParam: searchParams.get('type')?.trim() || 'first',
      battleCountdownPreviewSec,
    };
  }, []);
}

/**
 * Fusionne l’état battle socket avec une deadline factice pour prévisualiser le décompte d’inscription (URL `battleCountdownPreview`).
 * @param {object|null|undefined} battleState
 * @param {string|null} previewDeadlineIso
 */
function mergeBattleStateWithCountdownPreview(battleState, previewDeadlineIso) {
  if (!previewDeadlineIso) return battleState;

  const base =
    battleState && typeof battleState === 'object' ? battleState : {};
  const team1Hp = Number.isFinite(Number(base.team1Hp)) ? Number(base.team1Hp) : 100;
  const team2Hp = Number.isFinite(Number(base.team2Hp)) ? Number(base.team2Hp) : 100;
  const maxHp =
    Number.isFinite(Number(base.maxHp)) && Number(base.maxHp) > 0
      ? Number(base.maxHp)
      : 100;

  return {
    ...base,
    active: true,
    team1Hp,
    team2Hp,
    maxHp,
    registrationDeadlineAt: previewDeadlineIso,
    team1Members: Array.isArray(base.team1Members) ? base.team1Members : [],
    team2Members: Array.isArray(base.team2Members) ? base.team2Members : [],
  };
}

/**
 * Vue standalone (OBS) : interface minimale + overlay média + podium.
 */
function OverlayPlayerStandaloneView({
  connected,
  debug,
  currentOverlay,
  isPlaying,
  overlayQueue,
  birthdayExiting,
  videoRef,
  imageRef,
  leaderboardEntries,
  leaderboardConfig,
  leaderboardShowUntil,
  leaderboardEntering,
  leaderboardExiting,
  leaderboardUserIdParam,
  battleState,
}) {
  const showLeaderboard =
    !currentOverlay &&
    (leaderboardConfig.showWhen === 'always' ||
      (leaderboardConfig.showWhen === 'onRewardOnly' &&
        leaderboardShowUntil !== null));

  return (
    <div className="overlayPlayerStandalone">
      <BattleOverlay state={battleState} />
      {debug && (
        <>
          <div
            className={`statusIndicator ${
              connected ? 'connected' : 'disconnected'
            }`}
          >
            {connected ? '🟢' : '🔴'}
          </div>
          <div
            className={`connectionStatusDebug ${
              connected ? 'connected' : 'disconnected'
            }`}
          >
            {connected ? 'Connecté' : 'Déconnecté'}
          </div>
          {connected && (
            <div
              style={{
                fontSize: '10px',
                color: '#1db954',
                marginTop: '4px',
              }}
            >
              ✅ Prêt
            </div>
          )}
          {currentOverlay && (
            <div className="currentOverlayInfo">
              <span className="rewardTitle">
                {getOverlayDisplayTitle(currentOverlay)}
              </span>
              {currentOverlay.overlayType !== 'birthday' && (
                <span className="userName">par {currentOverlay.userName}</span>
              )}
            </div>
          )}
          {isPlaying && <div className="playingIndicator">🎬</div>}
        </>
      )}
      <div
        className={`overlayContainer${
          currentOverlay &&
          (currentOverlay.overlayType === 'video' ||
            currentOverlay.overlayType === 'image')
            ? ' overlayContainer--media'
            : ''
        }`}
      >
        {currentOverlay?.overlayType === 'video' && (
          <video ref={videoRef} className="overlayVideo" autoPlay playsInline />
        )}
        {currentOverlay?.overlayType === 'image' && (
          <img
            ref={imageRef}
            className="overlayImage"
            alt={currentOverlay.rewardTitle}
          />
        )}
        {currentOverlay?.overlayType === 'birthday' && (
          <BirthdayOverlayContent
            birthdays={currentOverlay.birthdays || []}
            exiting={birthdayExiting}
          />
        )}
      </div>
      {showLeaderboard && (
        <OverlayLeaderboardPodium
          entries={leaderboardEntries}
          rewardName={leaderboardConfig.rewardName}
          entering={leaderboardEntering}
          exiting={leaderboardExiting}
        />
      )}
    </div>
  );
}

/**
 * Vue dashboard : titre, statut, URL OBS, file d'attente, aperçu overlay actuel.
 */
function OverlayPlayerDashboardView({
  connected,
  currentOverlay,
  overlayQueue,
  isPlaying,
  birthdayExiting,
  videoRef,
  imageRef,
}) {
  return (
    <div className="overlayPlayerContainer">
      <div className="overlayPlayerHeader">
        <h3 className="overlayPlayerTitle">🎬 Lecteur d&apos;Overlays</h3>
        <div
          className={`connectionStatus ${
            connected ? 'connected' : 'disconnected'
          }`}
        >
          {connected ? 'Connecté' : 'Déconnecté'}
        </div>
      </div>

      <div className="overlayPlayerInfo">
        <p>
          Ce composant affiche automatiquement les overlays associés aux
          récompenses Twitch lorsqu&apos;elles sont échangées.
        </p>
        <p>
          <strong>Pour OBS :</strong> Ajoutez une source &quot;Navigateur&quot;
          avec l&apos;URL :
        </p>
        <code className="obsUrl">
          {typeof window !== 'undefined'
            ? `${window.location.origin}/overlay-player`
            : '/overlay-player'}
        </code>
        <p className="obsTip">
          Pour que l&apos;overlay fonctionne{' '}
          <strong>sans garder un onglet ouvert</strong> : dans OBS, clic droit
          sur la source → Propriétés → décocher &quot;Éteindre la source quand
          elle n&apos;est pas visible&quot;. Ainsi la source reste connectée
          même quand vous changez de scène.
        </p>
        <p className="obsTip">
          Si l&apos;overlay ou les médias n&apos;apparaissent pas : URL en
          HTTPS, rafraîchir la source (F5), vérifier que les fichiers sont en
          prod (uploads/overlays).
        </p>
        <p style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
          <strong>💡 Astuce son :</strong> Dans OBS, clic droit sur la source
          navigateur → Propriétés → Cocher &quot;Contrôler l&apos;audio via
          OBS&quot; pour activer le son.
        </p>
      </div>

      {!currentOverlay && overlayQueue.length === 0 && (
        <div className="noOverlayMessage">
          <p>Aucun overlay en cours</p>
        </div>
      )}

      {currentOverlay && (
        <div className="currentOverlayCard">
          <h4>Overlay actuel</h4>
          <div className="overlayDetails">
            <span className="rewardTitle">
              {getOverlayDisplayTitle(currentOverlay)}
            </span>
            {currentOverlay.overlayType !== 'birthday' && (
              <span className="userName">par {currentOverlay.userName}</span>
            )}
            <span className="timestamp">
              {new Date(currentOverlay.timestamp).toLocaleTimeString()}
            </span>
          </div>
          <div className="overlayPreview">
            {currentOverlay.overlayType === 'video' ? (
              <video
                ref={videoRef}
                className="overlayVideo"
                controls
                autoPlay
                playsInline
              />
            ) : currentOverlay.overlayType === 'birthday' ? (
              <BirthdayOverlayContent
                birthdays={currentOverlay.birthdays || []}
                exiting={birthdayExiting}
              />
            ) : (
              <img
                ref={imageRef}
                className="overlayImage"
                alt={currentOverlay.rewardTitle}
              />
            )}
          </div>
        </div>
      )}

      {overlayQueue.length > 0 && (
        <div className="queueInfo">
          <span>{overlayQueue.length} overlay(s) en attente</span>
        </div>
      )}

      {isPlaying && (
        <div className="playingStatus">
          <span className="playingIcon">🎬</span>
          <span>Affichage en cours...</span>
        </div>
      )}
    </div>
  );
}

/**
 * Composant principal : orchestration des hooks et rendu standalone ou dashboard.
 * @param {{ standalone?: boolean }} props
 */
export default function OverlayPlayer({ standalone = false }) {
  const {
    debug,
    leaderboardUserIdParam,
    leaderboardTypeParam,
    battleCountdownPreviewSec,
  } = useOverlayUrlParams();

  const playback = useOverlayPlayback();
  const {
    currentOverlay,
    overlayQueue,
    isPlaying,
    birthdayExiting,
    videoRef,
    imageRef,
    timeoutRef,
    handleOverlayShow,
  } = playback;

  const leaderboard = useLeaderboard(
    standalone,
    leaderboardUserIdParam,
    leaderboardTypeParam
  );
  const {
    leaderboardEntries,
    leaderboardConfig,
    leaderboardShowUntil,
    leaderboardEntering,
    leaderboardExiting,
    handleLeaderboardUpdate,
    leaderboardHideTimerRef,
  } = leaderboard;

  const battle = useBattleOverlay();
  const { battleState, onBattleStart, onBattleState, onBattleEnd } = battle;

  const previewRegistrationDeadlineIso = useMemo(() => {
    if (!battleCountdownPreviewSec || battleCountdownPreviewSec <= 0) {
      return null;
    }
    return new Date(Date.now() + battleCountdownPreviewSec * 1000).toISOString();
  }, [battleCountdownPreviewSec]);

  const displayBattleState = useMemo(
    () =>
      mergeBattleStateWithCountdownPreview(
        battleState,
        previewRegistrationDeadlineIso
      ),
    [battleState, previewRegistrationDeadlineIso]
  );

  const { connected } = useOverlaySocket(
    socketService,
    {
      onOverlayShow: handleOverlayShow,
      onLeaderboardUpdate: handleLeaderboardUpdate,
      onBattleStart,
      onBattleState,
      onBattleEnd,
    },
    { timeoutRef, leaderboardHideTimerRef }
  );

  if (standalone) {
    return (
      <OverlayPlayerStandaloneView
        connected={connected}
        debug={debug}
        currentOverlay={currentOverlay}
        isPlaying={isPlaying}
        overlayQueue={overlayQueue}
        birthdayExiting={birthdayExiting}
        videoRef={videoRef}
        imageRef={imageRef}
        leaderboardEntries={leaderboardEntries}
        leaderboardConfig={leaderboardConfig}
        leaderboardShowUntil={leaderboardShowUntil}
        leaderboardEntering={leaderboardEntering}
        leaderboardExiting={leaderboardExiting}
        leaderboardUserIdParam={leaderboardUserIdParam}
        battleState={displayBattleState}
      />
    );
  }

  return (
    <OverlayPlayerDashboardView
      connected={connected}
      currentOverlay={currentOverlay}
      overlayQueue={overlayQueue}
      isPlaying={isPlaying}
      birthdayExiting={birthdayExiting}
      videoRef={videoRef}
      imageRef={imageRef}
    />
  );
}
